import { z } from "zod";
import { type FieldDescriptor } from "./types.js";

/**
 * A scrollable terms box (or link) plus a mandatory checkbox. On acceptance the submit path writes
 * a `consent_record` capturing the exact policy version, timestamp and IP — "This is your
 * evidentiary record; a plain checkbox is not" (docs/01-PRODUCT-SPEC.md §3.2).
 *
 * `policyVersion` is part of the config rather than derived, because the evidentiary value comes
 * from knowing precisely which text someone agreed to. Changing the text without bumping the
 * version is the failure mode this exists to make visible.
 */
const configSchema = z.object({
  policyKey: z.string().min(1).default("terms_and_conditions"),
  policyVersion: z.string().min(1).default("1.0"),
  bodyText: z.string().max(100_000).default(""),
  externalUrl: z.string().url().optional(),
  acceptanceLabel: z.string().max(300).default("I have read and agree to the terms"),
});

export const termsField: FieldDescriptor<z.infer<typeof configSchema>> = {
  type: "terms",
  label: "Terms and conditions",
  category: "legal",
  icon: "ScrollText",
  configSchema,
  // Always literal `true`: a terms field that can be submitted unticked is not consent.
  valueSchema: () =>
    z.literal(true, { errorMap: () => ({ message: "You must accept to continue" }) }),
  defaultConfig: {
    policyKey: "terms_and_conditions",
    policyVersion: "1.0",
    bodyText: "",
    acceptanceLabel: "I have read and agree to the terms",
  },
  supportsProfileBinding: false,
  defaultSearchable: false,
  renderHints: {},
};
